import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IApplicationData } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from './applicationQueries';

/**
 * Returns a function that applies an immutable updater to the React Query application cache.
 *
 * Parallel to `useApplicationCache` (read-only). Use this as the low-level write primitive
 * inside named setter hooks, keeping `queryClient.setQueryData` out of component files.
 *
 * @example
 * const update = useApplicationUpdater();
 * update((old) => ({ ...old, information: { ...old.information, ... } }));
 */
export function useApplicationUpdater() {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const queryClient = useQueryClient();

    return (updater: (old: IApplicationData) => IApplicationData) => {
        queryClient.setQueryData<IApplicationData>(applicationQueryKey(entryGuid ?? ''), (old) => {
            if (!old) return old;
            return updater(old);
        });
    };
}
